import { useEffect, useRef, useState } from "react";
import {
  UserCog,
  Cpu,
  Feather,
  BadgeIndianRupee,
  Sparkles,
  HeartHandshake,
  Siren,
  Sofa,
} from "lucide-react";

const REASONS = [
  { icon: UserCog, title: "Experienced Doctors" },
  { icon: Cpu, title: "Latest Equipment" },
  { icon: Feather, title: "Pain-Free Treatments" },
  { icon: BadgeIndianRupee, title: "Affordable Pricing" },
  { icon: Sparkles, title: "Sterilized Instruments" },
  { icon: HeartHandshake, title: "Friendly Staff" },
  { icon: Siren, title: "Emergency Care" },
  { icon: Sofa, title: "Comfortable Environment" },
];

const COUNTERS = [
  { end: 1000, suffix: "+", label: "Happy Patients" },
  { end: 14, suffix: "+", label: "Dental Services" },
  { end: 4.3, suffix: "", label: "Google Rating", decimals: 1 },
  { end: 100, suffix: "%", label: "Sterilized" },
];

const Counter = ({ end, suffix = "", decimals = 0 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1600;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(end * eased);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [end]);

  return (
    <span ref={ref}>
      {decimals ? val.toFixed(decimals) : Math.round(val)}
      {suffix}
    </span>
  );
};

export const WhyChooseUs = () => {
  return (
    <section
      data-testid="why-choose-us-section"
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#009688]/10 text-[#009688] text-xs font-semibold tracking-wide uppercase">
              Why Choose Us
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937] leading-[1.05]">
              Reasons families in Manjeri{" "}
              <span className="text-[#009688]">trust Abhayam</span>.
            </h2>
            <p className="mt-5 text-lg text-[#1F2937]/65">
              Compassionate, transparent and precise dentistry — delivered by a
              team that puts patient comfort first.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {COUNTERS.map((c, i) => (
                <div
                  key={c.label}
                  data-testid={`counter-${i}`}
                  className="rounded-2xl bg-white border border-[#009688]/12 p-5"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-[#009688] font-[Outfit]">
                    <Counter end={c.end} suffix={c.suffix} decimals={c.decimals} />
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-[#1F2937]/55 font-semibold">
                    {c.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {REASONS.map(({ icon: Icon, title }, i) => (
                <div
                  key={title}
                  data-testid={`reason-card-${i}`}
                  className="group aspect-square bg-white rounded-2xl border border-[#009688]/10 p-4 flex flex-col justify-between hover:bg-[#009688] hover:border-[#009688] transition-all duration-300 shadow-[0_4px_20px_rgb(0,0,0,0.02)] hover:shadow-[0_16px_40px_rgba(0,150,136,0.25)]"
                >
                  <Icon
                    className="w-6 h-6 text-[#009688] group-hover:text-white transition-colors"
                    strokeWidth={1.8}
                  />
                  <div className="text-[13.5px] sm:text-sm font-semibold text-[#1F2937] group-hover:text-white transition-colors leading-snug">
                    {title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
